import {
  all,
  always,
  append,
  prepend,
  assoc,
  assocPath,
  concat,
  defaultTo,
  filter,
  includes,
  map,
  mergeDeepLeft,
  partialRight,
  pickAll,
  pipe,
  reduce,
  values,
  when,
} from 'ramda';

import { PAGE_TYPES } from 'constants/index';
import { formatRangeDates } from 'utils/datetime';
import { formatDaysRange } from 'utils/itineraryDetails';
import { SCHEDULE_TYPES } from 'utils/itineraryDetails/constants';
import { fork, getIn, isDefined, isNotDefined, rejectIndexed as reject } from 'utils/ramda';

import { BLOCK_ROLE } from '../Page/Blocks/Text';
import { ALLOWED_BLOCK_TYPES, RAW_PARAGRAPH, TYPES_COLLECTION } from './constants';

const isBlockHeader = (block) => block?.type === 'header';
const isAllowedBlockType = (type) => includes(type, ALLOWED_BLOCK_TYPES);

// external
export const isAllowedBlock = pipe(getIn('type'), isAllowedBlockType);

export const getParagraphsFromList = (data) =>
  data.items.reduce((acc, item) => {
    const titleParagraph = {
      ...RAW_PARAGRAPH,
      role: BLOCK_ROLE.LINE,
      data: { title: item.title },
    };

    return pipe(
      assoc('data', pickAll(['raw', 'text'], item.desc)),
      getFragmentsFromTextBlock,
      when(always(item.title), prepend(titleParagraph)),
      concat(acc),
    )(RAW_PARAGRAPH);
  }, []);

export function getFragmentsFromTextBlock(block, splitEveryBlock) {
  const paragraphs = paragraphise(block?.data?.raw?.blocks, splitEveryBlock);
  let order = 0;
  let wasListItem = false;

  const blockFactory = (block) => ({
    ...block,
    data: {
      raw: {
        blocks: [],
        entityMap: block?.data?.raw?.entityMap,
      },
      text: block?.text,
    },
  });

  return paragraphs.reduce((acc, cur, i) => {
    const newBlock = blockFactory(block);

    if (cur[0]?.type === 'unordered-list-item') {
      const styleOffset = 2;
      const inlineStyles = cur[0].inlineStyleRanges || [];
      const entities = cur[0].entityRanges || [];
      cur[0].text = `• ${cur[0].text}`;
      cur[0].type = 'unstyled';
      cur[0].inlineStyleRanges = inlineStyles.map((style) => ({ ...style, offset: style.offset + styleOffset }));
      cur[0].entityRanges = entities.map((elem) => ({ ...elem, offset: elem.offset + styleOffset }));
    }

    if (cur[0]?.type === 'ordered-list-item') {
      order += 1;
      const styleOffset = String(order)?.length + 2;
      const inlineStyles = cur[0].inlineStyleRanges || [];
      const entities = cur[0].entityRanges || [];
      cur[0].text = `${order}. ${cur[0].text}`;
      cur[0].type = 'unstyled';
      cur[0].inlineStyleRanges = inlineStyles.map((style) => ({ ...style, offset: style.offset + styleOffset }));
      cur[0].entityRanges = entities.map((elem) => ({ ...elem, offset: elem.offset + styleOffset }));
      wasListItem = true;
    } else if (wasListItem) {
      order = 0;
    }

    if (cur.length === 1 && isSNLBlock(cur[0])) {
      newBlock.data.text = ' ';
      delete newBlock.data.raw;
    } else {
      newBlock.data.raw.blocks = cur;
    }

    if (i < paragraphs.length - 1) {
      newBlock.role = BLOCK_ROLE.LINE;
    }

    acc.push(newBlock);

    return acc;
  }, []);
}

export function paragraphise(blocks = [], splitEveryBlock) {
  let buffer = [];

  return blocks.reduce((acc, block, i) => {
    if (splitEveryBlock) {
      if (block.text.includes('\n')) {
        block.text.split('\n').forEach((t) => {
          acc.push([{ ...block, text: t === '' ? ' ' : t, isTextLine: true }]);
        });
      } else {
        acc.push([{ ...block, isTextLine: true }]);
      }
      buffer = [];
    } else if (isSNLBlock(block)) {
      if (buffer.length > 0) {
        acc.push(buffer);
        buffer = [];
      }
    } else {
      buffer.push(block);
    }

    if (i === blocks.length - 1 && buffer.length > 0) acc.push(buffer);

    return acc;
  }, []);
}

// is Soft New Line block
export const isSNLBlock = (b) =>
  isNotDefined(b.text) &&
  TYPES_COLLECTION.includes(b.type) &&
  isNotDefined(b.data) &&
  isNotDefined(b.entityRanges) &&
  isNotDefined(b.inlineStyleRanges);

export function getParagraphsFromAccordion({ spoilers }) {
  const accumulator = (acc, { body, title }) => {
    if (isDefined(body?.raw))
      return pipe(
        assoc('data', pickAll(['raw', 'text'], body)),
        partialRight(getFragmentsFromTextBlock, [true]),
        when(isDefined, assocPath([0, 'data', 'title'], title)),
        concat(acc),
      )(RAW_PARAGRAPH);

    if (isDefined(body?.placeholder))
      return pipe(assoc('role', BLOCK_ROLE.LINE), assoc('data', pickAll(['placeholder', 'title'])), (block) =>
        append(block, acc),
      )(RAW_PARAGRAPH);

    if (isDefined(body?.text))
      return pipe(assoc('role', BLOCK_ROLE.LINE), assoc('data', pickAll(['text', 'title'])), (block) =>
        append(block, acc),
      )(RAW_PARAGRAPH);

    return acc;
  };

  return spoilers.reduce(accumulator, []);
}

const blockResolver = (block) => {
  if (block.type === 'list') return getParagraphsFromList(block.data);
  if (block.type === 'text') return getFragmentsFromTextBlock(block, true);
  if (block.type === 'accordion') return getParagraphsFromAccordion(block.data);

  return [block];
};

const blockAccumulator = (acc, block) => concat(acc, blockResolver(block));

export const getPreparedPageBlocks = pipe(defaultTo([]), filter(isAllowedBlock), reduce(blockAccumulator, []));

const isNoDate = pipe(values, all(isNotDefined));

export const getTitle = (page, dateFormat) => {
  const pageTitle = page?.title || page.headline;

  const accessToShowDate = (page) => includes(page.type, [PAGE_TYPES.AREA, PAGE_TYPES.ACTIVITY, PAGE_TYPES.PROPERTY]);

  const getTitleWithTimeStamp = ({ datesRange, daysRange, scheduleType }) => {
    if (scheduleType === SCHEDULE_TYPES.DATES) {
      if (isNoDate(datesRange)) return pageTitle;

      const formattedDate = formatRangeDates(dateFormat)([datesRange.start, datesRange.end]);

      return formattedDate ? `${formattedDate} | ${pageTitle}` : pageTitle;
    }

    if (scheduleType === SCHEDULE_TYPES.DAYS) {
      if (isNoDate(daysRange)) return pageTitle;

      const formattedDays = formatDaysRange(daysRange);

      return formattedDays ? `${formattedDays} | ${pageTitle}` : pageTitle;
    }

    return pageTitle;
  };

  if (accessToShowDate(page)) return getTitleWithTimeStamp(page);

  return pageTitle;
};

export function prepareBlocks(pages) {
  const getPageInfo = (page) => ({
    pageTitle: getTitle(page),
    pageType: page.type,
    primaryImageCoords: page.primaryImageCoords,
    image: page.primaryImageVersion800 || page.primaryImageUrl,
  });

  const getBlocksWithPageInfo = fork(
    (blocks, pageInfo) => map(mergeDeepLeft(pageInfo), blocks),
    pipe(getIn('blocks'), getPreparedPageBlocks),
    getPageInfo,
  );

  const isHeaderRepeated = (element, index, _blocks) => isBlockHeader(element) && isBlockHeader(_blocks[index + 1]);

  return pipe(
    defaultTo([]),
    map(getBlocksWithPageInfo),
    reject(isNotDefined),
    reduce(concat, []),
    reject(isHeaderRepeated),
  )(pages);
}
